import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { createVehicleService } from "@/lib/reseller-api";
import { updateMaintenance, type Device, type Maintenance, type Position } from "@/lib/traccar-api";
import { getLinkedDeviceId, isPresetServiceType, serviceValueFor, SERVICE_TYPES } from "@/lib/maintenance-status";

interface ServiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  devices: Device[];
  positionByDeviceId: Map<number, Position>;
  /** When set, the service completes this reminder and moves its next due point forward. */
  maintenance?: Maintenance | null;
}

const optionalNumber = (value: FormDataEntryValue | null) => {
  const text = String(value ?? "").trim();
  return text === "" ? null : Number(text);
};

export function ServiceDialog({ open, onOpenChange, devices, positionByDeviceId, maintenance }: ServiceDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const presetDeviceId = maintenance ? getLinkedDeviceId(maintenance) : null;
  const presetPosition = presetDeviceId ? positionByDeviceId.get(presetDeviceId) : undefined;
  const presetOdometer = presetPosition?.attributes?.totalDistance;
  const presetHours = presetPosition?.attributes?.hours;
  // From a reminder, default to its name as a custom type unless it is a preset key.
  const presetType = maintenance?.name ?? "";
  const [serviceType, setServiceType] = useState(
    !presetType ? "" : isPresetServiceType(presetType) ? presetType : "other",
  );

  const mutation = useMutation({
    mutationFn: async (service: Parameters<typeof createVehicleService>[0]) => {
      const created = await createVehicleService(service);
      if (maintenance) {
        const value = serviceValueFor(maintenance.type, service);
        if (value != null) {
          await updateMaintenance({
            ...maintenance,
            start: maintenance.period > 0 ? value + maintenance.period : maintenance.start,
            attributes: { ...(maintenance.attributes ?? {}), lastServiceAt: service.performed_at, lastServiceValue: value },
          });
        }
      }
      return created;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vehicle-services"] });
      queryClient.invalidateQueries({ queryKey: ["maintenances"] });
      onOpenChange(false);
      toast({ title: t("reseller.maintenance.serviceRegistered") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    mutation.mutate({
      device_id: Number(formData.get("device_id")),
      maintenance_id: maintenance?.id ?? null,
      service_type: serviceType === "other" ? (formData.get("service_type_custom") as string).trim() : serviceType,
      performed_at: formData.get("performed_at") as string,
      odometer_km: optionalNumber(formData.get("odometer_km")),
      engine_hours: optionalNumber(formData.get("engine_hours")),
      cost: optionalNumber(formData.get("cost")),
      workshop: (formData.get("workshop") as string) || null,
      notes: (formData.get("notes") as string) || null,
    });
  };

  // The reading that resets the reminder is required, otherwise it can't move forward.
  const needsOdometer = maintenance?.type === "totalDistance";
  const needsHours = maintenance?.type === "hours";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{t("reseller.maintenance.registerService")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="service_device_id">{t("reseller.fieldService.vehicle")} *</Label>
              <Select name="device_id" required defaultValue={presetDeviceId ? String(presetDeviceId) : undefined} disabled={Boolean(presetDeviceId)}>
                <SelectTrigger id="service_device_id">
                  <SelectValue placeholder={t("reseller.maintenance.selectVehicle")} />
                </SelectTrigger>
                <SelectContent>
                  {devices.map((device) => (
                    <SelectItem key={device.id} value={String(device.id)}>{device.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Disabled selects aren't submitted with the form. */}
              {presetDeviceId && <input type="hidden" name="device_id" value={presetDeviceId} />}
            </div>
            <div className="space-y-2">
              <Label htmlFor="service_type">{t("reseller.fieldService.serviceType")} *</Label>
              <Select name="service_type" required value={serviceType} onValueChange={setServiceType}>
                <SelectTrigger id="service_type">
                  <SelectValue placeholder={t("reseller.maintenance.selectServiceType")} />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>{t(`reseller.maintenance.serviceTypes.${type}`)}</SelectItem>
                  ))}
                  <SelectItem value="other">{t("reseller.maintenance.serviceTypes.other")}</SelectItem>
                </SelectContent>
              </Select>
              {serviceType === "other" && (
                <Input
                  name="service_type_custom"
                  required
                  autoFocus={!presetType}
                  placeholder={t("reseller.maintenance.customServiceType")}
                  defaultValue={presetType && !isPresetServiceType(presetType) ? presetType : ""}
                />
              )}
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="performed_at">{t("reseller.maintenance.performedAt")} *</Label>
              <Input id="performed_at" name="performed_at" type="date" required defaultValue={new Date().toISOString().slice(0, 10)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="odometer_km">{t("reseller.maintenance.odometer")}{needsOdometer && " *"}</Label>
              <Input
                id="odometer_km"
                name="odometer_km"
                type="number"
                min="0"
                step="1"
                required={needsOdometer}
                defaultValue={typeof presetOdometer === "number" ? Math.round(presetOdometer / 1000) : ""}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="engine_hours">{t("reseller.maintenance.engineHours")}{needsHours && " *"}</Label>
              <Input
                id="engine_hours"
                name="engine_hours"
                type="number"
                min="0"
                step="0.1"
                required={needsHours}
                defaultValue={typeof presetHours === "number" ? Math.round(presetHours / 360000) / 10 : ""}
              />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="cost">{t("reseller.maintenance.cost")}</Label>
              <Input id="cost" name="cost" type="number" min="0" step="0.01" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="workshop">{t("reseller.maintenance.workshop")}</Label>
              <Input id="workshop" name="workshop" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="service_notes">{t("reseller.fieldService.notes")}</Label>
            <Textarea id="service_notes" name="notes" rows={2} />
          </div>
          {maintenance && maintenance.period > 0 && (
            <p className="text-xs text-muted-foreground">{t("reseller.maintenance.resetHint")}</p>
          )}
          <Button type="submit" className="w-full" disabled={mutation.isPending}>
            {t("reseller.maintenance.registerService")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
